"use client";

import React from "react";
import { CircleCheck, Circle, Clock, Target } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import { Button } from "@/components/ui/button";
import { Link } from "@/components/common/Link";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
import { cn } from "@/lib/utils";
const roadmapData = [{
  year: "2025",
  title: "Foundation Establishment",
  description: "Formalizing the COHCC Foundation's core structure, launching initial grant programs, and establishing key community partnerships to build a strong operational base.",
  progress: 100,
  status: "completed"
}, {
  year: "2026",
  title: "Youth & Education Expansion",
  description: "Scaling our scholarship programs and launching the 'Future Leaders' mentorship initiative to support 500+ Hispanic students across Colorado high schools and universities.",
  progress: 65,
  status: "in-progress"
}, {
  year: "2027",
  title: "Statewide Entrepreneurship Incubator",
  description: "Opening regional satellite offices to provide bilingual business coaching, capital access workshops, and technical assistance to rural Hispanic entrepreneurs.",
  progress: 30,
  status: "planned"
}, {
  year: "2028",
  title: "Community Center Capital Campaign",
  description: "Initiating the fundraising and design phase for a dedicated Hispanic Community Center in Denver, serving as a hub for culture, business, and collaboration.",
  progress: 10,
  status: "planned"
}, {
  year: "2029",
  title: "Long-term Endowment Goals",
  description: "Achieving a sustainable endowment fund to ensure perpetual support for our programs, reducing reliance on annual fundraising and securing our legacy.",
  progress: 0,
  status: "planned"
}];
export default function StrategicRoadmap() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  return <section id="strategic-roadmap" ref={ref} className="bg-background relative overflow-hidden py-24 md:py-32">
      {}
      <div className="bg-secondary/5 absolute top-0 left-0 -z-10 h-full w-full opacity-50" />
      <div className="absolute -top-24 -right-24 -z-10 h-96 w-96 rounded-full bg-blue-900/5 blur-3xl" />
      <div className="absolute -bottom-24 -left-24 -z-10 h-96 w-96 rounded-full bg-amber-500/5 blur-3xl" />

      <div className="container mx-auto px-4 md:px-6">
        {}
        <div className="mb-20 text-center">
          <h2 className="text-primary font-serif text-4xl font-bold tracking-tight md:text-5xl">
            Strategic Roadmap
          </h2>
          <div className="bg-secondary mx-auto mt-6 h-1.5 w-24 rounded-full" />
          <p className="text-muted-foreground font-sans mx-auto mt-6 max-w-2xl text-lg">
            Our 5-year vision for empowering Colorado’s Hispanic community. We
            are committed to transparent growth, measurable impact, and
            sustainable progress.
          </p>
        </div>

        {}
        <div className="relative mx-auto max-w-5xl">
          {}
          <div className="bg-secondary/30 absolute left-8 top-0 bottom-0 w-1 -translate-x-1/2 md:left-1/2"></div>

          <div className="space-y-12 md:space-y-24">
            {roadmapData.map((item, index) => {
            const isEven = index % 2 === 0;
            const isCompleted = item.status === "completed";
            const isInProgress = item.status === "in-progress";
            return <div key={index} data-index={index} className={cn("relative flex flex-col md:flex-row md:items-center", isEven ? "md:flex-row-reverse" : "", isVisible ? "animate-in fade-in slide-in-from-bottom-8 duration-700 fill-mode-forwards" : "opacity-0")} style={{
              animationDelay: `${index * 150}ms`
            }}>
                  {}
                  <div className="hidden md:block md:w-1/2" />

                  {}
                  <div className="absolute left-8 z-10 flex h-12 w-12 -translate-x-1/2 items-center justify-center rounded-full border-4 border-white bg-white shadow-md md:left-1/2">
                    <div className={cn("flex h-full w-full items-center justify-center rounded-full transition-colors duration-500", isCompleted ? "bg-primary text-white" : isInProgress ? "bg-secondary text-primary" : "bg-muted text-muted-foreground")}>
                      {isCompleted ? <CircleCheck className="h-5 w-5" /> : isInProgress ? <Target className="h-5 w-5" /> : <Circle className="h-4 w-4" />}
                    </div>
                  </div>

                  {}
                  <div className={cn("ml-16 md:ml-0 md:w-1/2", isEven ? "md:pr-16" : "md:pl-16")}>
                    <Card className="border-border/50 shadow-lg transition-shadow duration-300 hover:shadow-xl">
                      <CardHeader className="pb-2">
                        <div className="flex items-center justify-between">
                          <span className="text-secondary font-serif text-xl font-bold">
                            {item.year}
                          </span>
                          {isInProgress && <span className="bg-secondary/20 text-primary-foreground rounded-full px-2 py-0.5 text-xs font-medium uppercase tracking-wider text-blue-900">
                              Current Focus
                            </span>}
                        </div>
                        <CardTitle className="text-primary font-serif text-2xl">
                          {item.title}
                        </CardTitle>
                      </CardHeader>
                      <CardContent>
                        <p className="text-muted-foreground font-sans mb-6 leading-relaxed">
                          {item.description}
                        </p>

                        <div className="space-y-2">
                          <div className="flex justify-between text-sm font-medium">
                            <span className="text-primary">
                              Strategic Progress
                            </span>
                            <span className="text-muted-foreground">
                              {item.progress}%
                            </span>
                          </div>
                          <Progress value={item.progress} className="h-2 bg-slate-100" aria-label={`Progress for ${item.title}`} />
                        </div>
                      </CardContent>
                    </Card>
                  </div>
                </div>;
          })}
          </div>
        </div>

        {}
        <div className="mt-24 text-center">
          <Button asChild size="lg" className="bg-primary hover:bg-primary/90 text-white">
            <Link to="/GetInvolvedPage">
              Partner With Us on This Journey
              <Clock className="ml-2 h-4 w-4" />
            </Link>
          </Button>
        </div>
      </div>
    </section>;
}